import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Spin,
  Tooltip,
  Switch,
  Divider,
  Alert,
  Card,
  Avatar,
  Flex,
  Space,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import DashboardService from '../../service';
import { shouldShowNoteField, getNoteVisibilityText, getNotePlaceholder } from '../../utils/noteVisibility';
import { useNotification } from '../../hooks/useNotification';
import { useTranslation } from '../../i18n/TranslationContext';

const SubdealerModal = ({ open, onClose, editingSubdealer, onSave }) => {
  const { user } = useAuth();
  const { showError } = useNotification();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [managers, setManagers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [copartAccounts, setCopartAccounts] = useState([]);
  const [iaaiAccounts, setIaaiAccounts] = useState([]);
  const [iaaiCanadaAccounts, setIaaiCanadaAccounts] = useState([]);
  const [manheimAccounts, setManheimAccounts] = useState([]);
  const [acvAccounts, setAcvAccounts] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    branchId: '',
    managerId: '',
    dealerId: '',
    copartId: '',
    copartIdSecondary: '',
    iaaiId: '',
    iaaiIdSecondary: '',
    iaaiCanadaId: '',
    iaaiCanadaIdSecondary: '',
    manheimId: '',
    manheimIdSecondary: '',
    acvId: '',
    acvIdSecondary: '',
    status: 'active',
    note: '',
    copartOn: false,
    iaaiOn: false,
    iaaiCanadaOn: false,
    manheimOn: false,
    acvOn: false,
    prebid: true,
    liveBid: true,
    unlimited: false,
    bidLimit: '0',
    activeBidsLimit: '0',
    activeBidsSumLimit: '0',
    purchasedCarsLimit: '0',
    lotsEventSetting: 'hide',
    purchasedLotsSetting: 'winning_lots',
    packageSlug: 'run_drive',
    show_calculator: true,
    show_helper: false,
    show_discount: false,
    discountAmount: 0
  });

  useEffect(() => {
    if (open) {
      loadData();
    }

    if (editingSubdealer) {
      setFormData({
        username: editingSubdealer.user_name || '',
        password: '',
        confirmPassword: '',
        email: editingSubdealer.email || '',
        firstName: editingSubdealer.firstName || '',
        lastName: editingSubdealer.lastName || '',
        phone: editingSubdealer.phone || '',
        branchId: editingSubdealer.branchId || '',
        managerId: editingSubdealer.managerId || '',
        dealerId: editingSubdealer.dealerId || '',
        copartId: editingSubdealer.copart_id || '',
        copartIdSecondary: editingSubdealer.copart_id_secondary || '',
        iaaiId: editingSubdealer.iaai_id || '',
        iaaiIdSecondary: editingSubdealer.iaai_id_secondary || '',
        iaaiCanadaId: editingSubdealer.iaai_canada_id || '',
        iaaiCanadaIdSecondary: editingSubdealer.iaai_canada_id_secondary || '',
        manheimId: editingSubdealer.manheim_id || '',
        manheimIdSecondary: editingSubdealer.manheim_id_secondary || '',
        acvId: editingSubdealer.acv_id || '',
        acvIdSecondary: editingSubdealer.acv_id_secondary || '',
        status: editingSubdealer.status || 'active',
        note: editingSubdealer.note !== undefined ? (editingSubdealer.note || '') : '',
        copartOn: editingSubdealer.copart_on !== undefined ? editingSubdealer.copart_on : false,
        iaaiOn: editingSubdealer.iaai_on !== undefined ? editingSubdealer.iaai_on : false,
        iaaiCanadaOn: editingSubdealer.iaai_canada_on !== undefined ? editingSubdealer.iaai_canada_on : false,
        manheimOn: editingSubdealer.manheim_on !== undefined ? editingSubdealer.manheim_on : false,
        acvOn: editingSubdealer.acv_on !== undefined ? editingSubdealer.acv_on : false,
        prebid: editingSubdealer.prebid !== undefined ? editingSubdealer.prebid : true,
        liveBid: editingSubdealer.live_bid !== undefined ? editingSubdealer.live_bid : true,
        unlimited: editingSubdealer.unlimited !== undefined ? editingSubdealer.unlimited : false,
        bidLimit: editingSubdealer.bet_limit?.limit?.toString() || '0',
        activeBidsLimit: editingSubdealer.bet_limit?.active_bids_limit?.toString() || '0',
        activeBidsSumLimit: editingSubdealer.bet_limit?.active_bids_sum_limit?.toString() || '0',
        purchasedCarsLimit: editingSubdealer.pay_limit?.count_limit?.toString() || '0',
        lotsEventSetting: editingSubdealer.lots_event_setting || 'hide',
        purchasedLotsSetting: editingSubdealer.purchased_lots_setting || 'winning_lots',
        packageSlug: editingSubdealer.package_slug || 'run_drive',
        show_calculator: editingSubdealer.show_calculator !== undefined ? editingSubdealer.show_calculator : true,
        show_helper: editingSubdealer.show_helper !== undefined ? editingSubdealer.show_helper : false,
        show_discount: editingSubdealer.show_discount !== undefined ? editingSubdealer.show_discount : false,
        discountAmount: editingSubdealer.discountAmount || 0
      });
    } else {
      if (user.role === 'manager') {
        setFormData(prev => ({
          ...prev,
          branchId: user.branchId,
          managerId: user.user_id
        }));
      } else if (user.role === 'dealer') {
        setFormData(prev => ({
          ...prev,
          branchId: user.branchId,
          managerId: user.managerId,
          dealerId: user.user_id
        }));
      }
    }
  }, [editingSubdealer, user, open]);

  useEffect(() => {
    if (open && !editingSubdealer && user.role === 'super_admin') {
      if (branches.length === 1 && !formData.branchId) {
        setFormData(prev => ({
          ...prev,
          branchId: branches[0].id.toString()
        }));
      }
      if (managers.length === 1 && !formData.managerId) {
        setFormData(prev => ({
          ...prev,
          managerId: managers[0].user_id || managers[0].id
        }));
      }
    }
  }, [open, editingSubdealer, branches, managers, formData.branchId, formData.managerId, user.role]);

  useEffect(() => {
    if (open && !editingSubdealer) {
      setFormData(prev => {
        const newData = { ...prev };
        let changed = false;

        const getFirstAccount = (accounts) => {
          const valid = accounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type);
          return valid.length > 0 ? (valid[0]._id || valid[0].id) : '';
        };

        if (!newData.copartId && copartAccounts.length > 0) {
          const first = getFirstAccount(copartAccounts);
          if (first) { newData.copartId = first; changed = true; }
        }
        if (!newData.iaaiId && iaaiAccounts.length > 0) {
          const first = getFirstAccount(iaaiAccounts);
          if (first) { newData.iaaiId = first; changed = true; }
        }
        if (!newData.iaaiCanadaId && iaaiCanadaAccounts.length > 0) {
          const first = getFirstAccount(iaaiCanadaAccounts);
          if (first) { newData.iaaiCanadaId = first; changed = true; }
        }
        if (!newData.manheimId && manheimAccounts.length > 0) {
          const first = getFirstAccount(manheimAccounts);
          if (first) { newData.manheimId = first; changed = true; }
        }
        if (!newData.acvId && acvAccounts.length > 0) {
          const first = getFirstAccount(acvAccounts);
          if (first) { newData.acvId = first; changed = true; }
        }

        return changed ? newData : prev;
      });
    }
  }, [open, editingSubdealer, copartAccounts, iaaiAccounts, iaaiCanadaAccounts, manheimAccounts, acvAccounts]);

  const loadData = async () => {
    try {
      const [managersData, dealersData, branchesData, accountsData] = await Promise.all([
        DashboardService.getManagers(1, 100, 'createdAt', 'desc'),
        DashboardService.getDealers(1, 100, 'createdAt', 'desc'),
        DashboardService.getBranches(),
        DashboardService.getAuctionAccounts({ page: 1, limit: 100, sortBy: 'created_at', sortOrder: 'desc' })
      ]);

      if (managersData.success && managersData.users) {
        setManagers(managersData.users);
      }
      if (dealersData.success && dealersData.users) setDealers(dealersData.users);
      if (branchesData.success && branchesData.branches) {
        setBranches(branchesData.branches);
      } else if (branchesData.branches) {
        setBranches(branchesData.branches);
      }

      if (accountsData.success && accountsData.accounts) {
        setCopartAccounts(accountsData.accounts.filter(acc => acc.auction?.toLowerCase() === 'copart'));
        setIaaiAccounts(accountsData.accounts.filter(acc => acc.auction?.toLowerCase() === 'iaai'));
        setIaaiCanadaAccounts(accountsData.accounts.filter(acc => acc.auction?.toLowerCase() === 'iaai_canada' || acc.auction?.toLowerCase() === 'iaai canada'));
        setManheimAccounts(accountsData.accounts.filter(acc => acc.auction?.toLowerCase() === 'manheim'));
        setAcvAccounts(accountsData.accounts.filter(acc => acc.auction?.toLowerCase() === 'acv'));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      showError('Failed to load data');
    }
  };

  const getAvailableDealers = () => {
    if (user.role === 'super_admin') {
      return dealers.filter(d =>
        !formData.branchId || d.branchId === parseInt(formData.branchId)
      );
    } else if (user.role === 'manager') {
      return dealers.filter(d =>
        d.managerId === user.user_id && d.branchId === user.branchId
      );
    } else if (user.role === 'dealer') {
      return dealers.filter(d => d.user_id === user.user_id);
    }
    return [];
  };

  const getAvailableManagers = () => {
    if (user.role === 'super_admin') {
      return managers.filter(m =>
        !formData.branchId || m.branchId === parseInt(formData.branchId)
      );
    } else if (user.role === 'manager') {
      return managers.filter(m => m.user_id === user.user_id);
    } else if (user.role === 'dealer') {
      return managers.filter(m => m.user_id === user.managerId);
    }
    return [];
  };

  const hasAuctionAccount = () => {
    return (formData.copartId && formData.copartId !== '' && formData.copartOn) ||
      (formData.iaaiId && formData.iaaiId !== '' && formData.iaaiOn) ||
      (formData.iaaiCanadaId && formData.iaaiCanadaId !== '' && formData.iaaiCanadaOn) ||
      (formData.manheimId && formData.manheimId !== '' && formData.manheimOn) ||
      (formData.acvId && formData.acvId !== '' && formData.acvOn);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingSubdealer && !hasAuctionAccount()) {
      showError('Please select at least one auction account (Copart or IAAI)');
      return;
    }

    if (!editingSubdealer) {
      if (formData.password !== formData.confirmPassword) {
        showError('Passwords do not match!');
        return;
      }
      if (formData.password.length < 6) {
        showError('Password must be at least 6 characters');
        return;
      }
    } else {
      if (formData.password && formData.password.length > 0) {
        if (formData.password !== formData.confirmPassword) {
          showError('Passwords do not match!');
          return;
        }
        if (formData.password.length < 6) {
          showError('Password must be at least 6 characters');
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      await onSave(formData, editingSubdealer);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      username: '',
      password: '',
      confirmPassword: '',
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      branchId: '',
      managerId: '',
      dealerId: '',
      status: 'active',
      note: ''
    });
    onClose();
  };

  const AuctionAccountCard = ({ title, shortLabel, color, on, setOn, primaryValue, primaryOptions, onPrimaryChange, secondaryValue, secondaryOptions, onSecondaryChange }) => (
    <Card size="small" style={{ height: '100%', transition: 'all 0.3s ease', borderColor: on ? color : undefined }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={12}>
          <Avatar style={{ backgroundColor: on ? color : '#bdbdbd', width: 32, height: 32, fontSize: '0.9rem' }}>{shortLabel}</Avatar>
          <Typography.Text strong style={{ fontSize: '1rem', color: on ? color : '#757575' }}>{title}</Typography.Text>
        </Flex>
        <Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{on ? 'On' : 'Off'}</Typography.Text>
          <Switch checked={on} onChange={setOn} size="small" />
        </Space>
      </Flex>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Select
          placeholder={t('dealers.accountAmPlaceholder')}
          value={primaryValue || undefined}
          onChange={onPrimaryChange}
          disabled={!on}
          size="small"
          style={{ width: '100%' }}
          options={primaryOptions.map(acc => ({ value: acc._id || acc.id, label: `${acc.account} - Buyer #${acc.buyer_number}` }))}
        />
        <Select
          placeholder={t('dealers.accountUsOptional')}
          value={secondaryValue === '' ? '' : (secondaryValue || undefined)}
          onChange={(v) => onSecondaryChange(v === undefined || v === null ? '' : (v || ''))}
          disabled={!on}
          size="small"
          style={{ width: '100%' }}
          allowClear
          options={[{ value: '', label: 'None' }, ...secondaryOptions.map(acc => ({ value: acc._id || acc.id, label: `${acc.account} - Buyer #${acc.buyer_number}` }))]}
        />
      </Space>
    </Card>
  );

  return (
    <Modal
      title={editingSubdealer ? 'Edit Subdealer' : 'Create New Subdealer'}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <form onSubmit={handleSubmit}>
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item label="First Name" required>
                <Input
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="e.g., Giorgi"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Last Name" required>
                <Input
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="e.g., Mamardashvili"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Username" required>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="e.g., subdealer.ge1.1"
                  disabled={!!editingSubdealer}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label={editingSubdealer ? "Password (leave empty to keep current)" : "Password"} required={!editingSubdealer}>
                <Input.Password
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  minLength={6}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Confirm Password" required={!editingSubdealer || (editingSubdealer && formData.password.length > 0)}>
                <Input.Password
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  status={formData.password !== formData.confirmPassword && formData.confirmPassword !== '' ? 'error' : undefined}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Email" required>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="subdealer@bidmanager.com"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Phone" required>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+995 555 111 001"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Branch" required>
                <Select
                  placeholder="Select branch"
                  value={formData.branchId || undefined}
                  onChange={(v) => setFormData({
                    ...formData,
                    branchId: v || '',
                    managerId: '',
                    dealerId: ''
                  })}
                  disabled={user.role !== 'super_admin'}
                  style={{ width: '100%' }}
                  options={branches.map(branch => ({ value: branch.id, label: `${branch.flag} ${branch.name}` }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Manager" required>
                <Select
                  placeholder="Select manager"
                  value={formData.managerId || undefined}
                  onChange={(v) => setFormData({ ...formData, managerId: v || '', dealerId: '' })}
                  disabled={user.role !== 'super_admin' || !formData.branchId}
                  style={{ width: '100%' }}
                  options={getAvailableManagers().map(manager => ({ value: manager.user_id, label: manager.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Assign to Dealer" required>
                <Select
                  placeholder="Select dealer"
                  value={formData.dealerId || undefined}
                  onChange={(v) => setFormData({ ...formData, dealerId: v || '' })}
                  disabled={user.role === 'dealer' || !formData.managerId}
                  style={{ width: '100%' }}
                  options={getAvailableDealers()
                    .filter(d => !formData.managerId || d.managerId === formData.managerId)
                    .map(dealer => ({ value: dealer.user_id, label: `${dealer.name} - ${dealer.branchName}` }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                Auction Accounts {!editingSubdealer && <Typography.Text type="danger">*</Typography.Text>}
              </Typography.Text>
              {!editingSubdealer && !hasAuctionAccount() && (
                <Typography.Text type="danger" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                  Please select at least one auction account.
                </Typography.Text>
              )}
            </Col>

            <Col xs={24} md={12}>
              <AuctionAccountCard
                title="Copart"
                shortLabel="C"
                color="#1976d2"
                on={formData.copartOn}
                setOn={(checked) => setFormData({ ...formData, copartOn: checked })}
                primaryValue={formData.copartId}
                primaryOptions={copartAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)}
                onPrimaryChange={(v) => setFormData({ ...formData, copartId: v || '' })}
                secondaryValue={formData.copartIdSecondary}
                secondaryOptions={copartAccounts.filter(acc => acc.type === 'secondary')}
                onSecondaryChange={(v) => setFormData({ ...formData, copartIdSecondary: v || '' })}
              />
            </Col>
            <Col xs={24} md={12}>
              <AuctionAccountCard
                title="IAAI"
                shortLabel="I"
                color="#d32f2f"
                on={formData.iaaiOn}
                setOn={(checked) => setFormData({ ...formData, iaaiOn: checked })}
                primaryValue={formData.iaaiId}
                primaryOptions={iaaiAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)}
                onPrimaryChange={(v) => setFormData({ ...formData, iaaiId: v || '' })}
                secondaryValue={formData.iaaiIdSecondary}
                secondaryOptions={iaaiAccounts.filter(acc => acc.type === 'secondary')}
                onSecondaryChange={(v) => setFormData({ ...formData, iaaiIdSecondary: v || '' })}
              />
            </Col>
            <Col xs={24} md={12}>
              <AuctionAccountCard
                title="IAAI (Canada)"
                shortLabel="IC"
                color="#d32f2f"
                on={formData.iaaiCanadaOn}
                setOn={(checked) => setFormData({ ...formData, iaaiCanadaOn: checked })}
                primaryValue={formData.iaaiCanadaId}
                primaryOptions={iaaiCanadaAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)}
                onPrimaryChange={(v) => setFormData({ ...formData, iaaiCanadaId: v || '' })}
                secondaryValue={formData.iaaiCanadaIdSecondary}
                secondaryOptions={iaaiCanadaAccounts.filter(acc => acc.type === 'secondary')}
                onSecondaryChange={(v) => setFormData({ ...formData, iaaiCanadaIdSecondary: v || '' })}
              />
            </Col>
            <Col xs={24} md={12}>
              <AuctionAccountCard
                title="Manheim"
                shortLabel="M"
                color="#ed6c02"
                on={formData.manheimOn}
                setOn={(checked) => setFormData({ ...formData, manheimOn: checked })}
                primaryValue={formData.manheimId}
                primaryOptions={manheimAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)}
                onPrimaryChange={(v) => setFormData({ ...formData, manheimId: v || '' })}
                secondaryValue={formData.manheimIdSecondary}
                secondaryOptions={manheimAccounts.filter(acc => acc.type === 'secondary')}
                onSecondaryChange={(v) => setFormData({ ...formData, manheimIdSecondary: v || '' })}
              />
            </Col>
            <Col xs={24} md={12}>
              <AuctionAccountCard
                title="ACV"
                shortLabel="A"
                color="#9c27b0"
                on={formData.acvOn}
                setOn={(checked) => setFormData({ ...formData, acvOn: checked })}
                primaryValue={formData.acvId}
                primaryOptions={acvAccounts.filter(acc => acc.type === 'primary' || acc.type === 'master' || !acc.type)}
                onPrimaryChange={(v) => setFormData({ ...formData, acvId: v || '' })}
                secondaryValue={formData.acvIdSecondary}
                secondaryOptions={acvAccounts.filter(acc => acc.type === 'secondary')}
                onSecondaryChange={(v) => setFormData({ ...formData, acvIdSecondary: v || '' })}
              />
            </Col>

            {(editingSubdealer ? shouldShowNoteField(editingSubdealer) : true) && (
              <Col xs={24}>
                <Form.Item label="Note" help={getNoteVisibilityText('subdealer')}>
                  <Input.TextArea
                    rows={3}
                    value={formData.note}
                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                    placeholder={getNotePlaceholder('subdealer')}
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24}>
              <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>
                Auction & Limits Configuration
              </Typography.Text>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={
                <span>
                  Bid Limit
                  <Tooltip title={t('subdealers.tooltips.bidLimit')}>
                    <Button type="text" size="small" icon={<InfoCircleOutlined />} style={{ marginLeft: 4 }} />
                  </Tooltip>
                </span>
              }>
                <Input
                  type="number"
                  value={formData.bidLimit}
                  onChange={(e) => setFormData({ ...formData, bidLimit: e.target.value })}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={
                <span>
                  Active Bids Amount
                  <Tooltip title={t('subdealers.tooltips.activeBidsLimit')}>
                    <Button type="text" size="small" icon={<InfoCircleOutlined />} style={{ marginLeft: 4 }} />
                  </Tooltip>
                </span>
              }>
                <Input
                  type="number"
                  value={formData.activeBidsLimit}
                  onChange={(e) => setFormData({ ...formData, activeBidsLimit: e.target.value })}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={
                <span>
                  Active Bids Sum Limit
                  <Tooltip title={t('subdealers.tooltips.activeBidsSumLimit')}>
                    <Button type="text" size="small" icon={<InfoCircleOutlined />} style={{ marginLeft: 4 }} />
                  </Tooltip>
                </span>
              }>
                <Input
                  type="number"
                  value={formData.activeBidsSumLimit}
                  onChange={(e) => setFormData({ ...formData, activeBidsSumLimit: e.target.value })}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={
                <span>
                  Purchased Cars Limit
                  <Tooltip title={t('subdealers.tooltips.purchasedCarsLimit')}>
                    <Button type="text" size="small" icon={<InfoCircleOutlined />} style={{ marginLeft: 4 }} />
                  </Tooltip>
                </span>
              }>
                <Input
                  type="number"
                  value={formData.purchasedCarsLimit}
                  onChange={(e) => setFormData({ ...formData, purchasedCarsLimit: e.target.value })}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={
                <span>
                  Lots Event Setting
                  <Tooltip title={t('subdealers.tooltips.lotsEventSetting')}>
                    <Button type="text" size="small" icon={<InfoCircleOutlined />} style={{ marginLeft: 4 }} />
                  </Tooltip>
                </span>
              }>
                <Select
                  value={formData.lotsEventSetting || 'hide'}
                  onChange={(v) => setFormData({ ...formData, lotsEventSetting: v || 'hide' })}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'hide', label: 'Hide' },
                    { value: 'show', label: 'Show' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={
                <span>
                  Purchased Lots Setting
                  <Tooltip title={t('subdealers.tooltips.purchasedLotsSetting')}>
                    <Button type="text" size="small" icon={<InfoCircleOutlined />} style={{ marginLeft: 4 }} />
                  </Tooltip>
                </span>
              }>
                <Select
                  value={formData.purchasedLotsSetting || 'winning_lots'}
                  onChange={(v) => setFormData({ ...formData, purchasedLotsSetting: v || 'winning_lots' })}
                  style={{ width: '100%' }}
                  options={[
                    { value: 'hide', label: 'Hide' },
                    { value: 'partlyShow', label: 'Partly Show' },
                    { value: 'winning_lots', label: 'Winning Lots' },
                    { value: 'show', label: 'Show' }
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Divider style={{ margin: '16px 0' }} />
              <Typography.Text strong style={{ display: 'block', marginBottom: 16 }}>
                Calculator Settings
              </Typography.Text>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Show Calculator Widget">
                <Switch
                  checked={formData.show_calculator}
                  onChange={(checked) => setFormData({ ...formData, show_calculator: checked })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Show Helper Tool">
                <Switch
                  checked={formData.show_helper}
                  onChange={(checked) => setFormData({ ...formData, show_helper: checked })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="Show Discount Row">
                <Switch
                  checked={formData.show_discount}
                  onChange={(checked) => setFormData({ ...formData, show_discount: checked })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Fixed Discount Amount ($)" help="Fixed discount amount in USD">
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={formData.discountAmount}
                  onChange={(e) => setFormData({ ...formData, discountAmount: parseFloat(e.target.value) || 0 })}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Alert
                message="Note: Sub-dealers do NOT inherit parent dealer discounts."
                type="info"
                showIcon
              />
            </Col>
          </Row>
        </div>

        <div style={{ paddingTop: 16, borderTop: '1px solid #f0f0f0', marginTop: 16 }}>
          <Space>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              disabled={submitting || (!editingSubdealer && !hasAuctionAccount())}
              loading={submitting}
            >
              {editingSubdealer ? 'Update' : 'Create'}
            </Button>
          </Space>
        </div>
      </form>
    </Modal>
  );
};

export default SubdealerModal;
